import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CSS/Launcher.css';

// Set this to true to test the Launcher with static sample data
const USE_MOCKS = false;

const MOCKED_EVENTS = [
  {
    label: "Euforia",
    url: "https://www.intoreality.tv/product/674f18ef614230482f93bca4",
    picture_url: "https://dzsyhtfpcz9op.cloudfront.net/staticResources/featuredImages/laura.png"
  },
  {
    label: "Palau Moja",
    url: "https://www.intoreality.tv/product/6992c9e6110c20a8e82c969d",
    picture_url: "https://dzsyhtfpcz9op.cloudfront.net/staticResources/featuredImages/palaumojaSlide.png"
  }
];

const Launcher = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  const handleCardClick = (url) => {
    if (!url) return;
    // Append launcher=true query parameter to instruct the player to AutoPlay
    const navigateUrl = url.includes('?') ? `${url}&launcher=true` : `${url}?launcher=true`;

    try {
      if (navigateUrl.startsWith('http')) {
        const parsed = new URL(navigateUrl);
        // Si la URL apunta a nuestra app/SPA (/product/), navegamos internamente sin recargar página
        // para conservar el token de activación de usuario de WebXR otorgado al iniciar el APK/PWA.
        if (parsed.origin === window.location.origin || parsed.pathname.startsWith('/product/')) {
          navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`);
          return;
        }
        window.location.href = navigateUrl;
      } else {
        navigate(navigateUrl);
      }
    } catch (e) {
      window.location.href = navigateUrl;
    }
  };

  useEffect(() => {
    // Add meta tag for PWA capability dynamically if needed
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', '#0f0f1d');

    // Prevent search engine indexing dynamically for this specific page
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');

    const fetchEvents = async () => {
      try {
        if (USE_MOCKS) {
          setEvents(MOCKED_EVENTS);
          if (MOCKED_EVENTS.length > 0 && MOCKED_EVENTS[0].url && !hasRedirected.current) {
            hasRedirected.current = true;
            handleCardClick(MOCKED_EVENTS[0].url);
            return;
          }
          setLoading(false);
          return;
        }

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/product/launcher`);

        if (!response.ok) {
          throw new Error('Failed to fetch VR events');
        }

        const data = await response.json();
        if (data.success) {
          setEvents(data.events);
          if (data.events && data.events.length > 0 && data.events[0].url && !hasRedirected.current) {
            hasRedirected.current = true;
            handleCardClick(data.events[0].url);
            return;
          }
        } else {
          throw new Error(data.message || 'Error fetching events');
        }
      } catch (err) {
        console.error('Launcher fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchSettings = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/launcher-settings`);
        const data = await response.json();
        if (data.success && data.settings) {
          setSettings(data.settings);
        }
      } catch (err) {
        console.error('Launcher settings fetch error:', err);
      }
    };

    fetchEvents();
    fetchSettings();

    return () => {
      // Revert theme color and robots on unmount
      if (metaTheme) metaTheme.setAttribute('content', '#000000');
      if (metaRobots) metaRobots.setAttribute('content', 'index, follow');
    }
  }, []);

  const dynamicStyles = settings ? {
    '--launcher-bg-start': settings.backgroundGradientStart || '#1a1a2e',
    '--launcher-bg-end': settings.backgroundGradientEnd || '#0f0f1d',
    '--launcher-title-start': settings.titleColorStart || '#00f2fe',
    '--launcher-title-end': settings.titleColorEnd || '#4facfe',
    '--launcher-title-size': `${settings.titleFontSize ?? 3}rem`,
    '--launcher-title-mt': `${settings.titleMarginTop ?? 0}rem`,
    '--launcher-title-mb': `${settings.titleMarginBottom ?? 2.5}rem`,
    '--launcher-play-start': settings.playButtonColorStart || '#00f2fe',
    '--launcher-play-end': settings.playButtonColorEnd || '#4facfe',
  } : {};

  return (
    <div className="launcher-container" style={dynamicStyles}>
      <div className="launcher-header">
        <h1>{settings?.titleText || 'VR Launcher'}</h1>
      </div>

      {loading && (
        <div className="launcher-loader">
          <div className="spinner"></div>
          <p>Loading Experiences...</p>
        </div>
      )}

      {error && (
        <div className="launcher-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="launcher-empty">
          <p>No experiences match your criteria right now.</p>
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="launcher-grid">
          {events.map((event, index) => (
            <div
              key={index}
              className="launcher-card"
              onClick={() => handleCardClick(event.url)}
            >
              <img
                src={event.picture_url || '/placeholder-event.png'}
                alt={event.label}
                className="launcher-card-bg"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22288%22%20height%3D%22225%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20288%20225%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_18%20text%20%7B%20fill%3A%23eceeef%3Bfont-weight%3Abold%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A14pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_18%22%3E%3Crect%20width%3D%22288%22%20height%3D%22225%22%20fill%3D%22%2355595c%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%2296.32500076293945%22%20y%3D%22118.8%22%3EThumbnail%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                }}
              />
              <div className="launcher-card-content">
                <h3 className="launcher-card-title">{event.label}</h3>
                <div className="launcher-play-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Launcher;